import { Link } from 'react-router-dom';
import { CopyIdButton } from '@/components/shared';
import { BackofficeTagChips } from './BackofficeTagChips';
import {
  formatDateTime,
  formatShortId,
  formatTransactionReferenceSource,
  formatTransactionTypeLabel,
} from '../format';
import type { BackofficeOrganizationTransactionRow } from '../types';

export interface OrganizationTransactionsTableProps {
  organizationName: string;
  transactions: BackofficeOrganizationTransactionRow[];
}

function formatQuantity(quantity: number): string {
  return quantity > 0 ? `+${quantity}` : String(quantity);
}

function renderContext(row: BackofficeOrganizationTransactionRow) {
  if (row.transactionType === 'usage' && row.interview) {
    return (
      <div className="flex flex-col gap-1">
        <Link
          to={`/interviews/${row.interview.id}`}
          className="text-small font-medium text-backoffice-700 hover:text-backoffice-800 hover:underline"
        >
          Interview {formatShortId(row.interview.id)}
        </Link>
        <span className="text-small text-neutral-500">
          {row.interview.studyTitle ?? 'Study not available'}
        </span>
      </div>
    );
  }

  return <span className="text-small text-neutral-500">Not applicable</span>;
}

function renderReference(row: BackofficeOrganizationTransactionRow) {
  const showInternalComment =
    row.reference.source === 'backoffice-bff.adjustments.id' && row.internalComment;
  const referenceId =
    row.transactionType === 'voucher' && row.reference.subId
      ? row.reference.subId
      : row.reference.id;
  const copyLabel =
    row.transactionType === 'voucher' && row.reference.subId
      ? 'Copy voucher ID'
      : 'Copy transaction reference ID';

  return (
    <div className="flex flex-col gap-1">
      <span className="text-small font-medium text-neutral-900">
        {formatTransactionReferenceSource(row.reference.source)}
      </span>
      <div className="flex items-center gap-2 text-small text-neutral-500">
        <span className="tabular-nums">{formatShortId(referenceId)}</span>
        <CopyIdButton value={referenceId} label={copyLabel} />
      </div>
      {row.actor ? (
        <span className="text-small text-neutral-500">{row.actor.name ?? row.actor.email}</span>
      ) : null}
      {row.transactionType === 'voucher' ? (
        <span className="text-small text-neutral-500">
          Redeemed by:{' '}
          {row.redeemedBy
            ? (row.redeemedBy.name ?? row.redeemedBy.email ?? formatShortId(row.redeemedBy.id))
            : 'Unknown'}
        </span>
      ) : null}
      {showInternalComment ? (
        <span className="text-small text-neutral-500">Internal comment: {row.internalComment}</span>
      ) : null}
    </div>
  );
}

export function OrganizationTransactionsTable({
  organizationName,
  transactions,
}: OrganizationTransactionsTableProps) {
  return (
    <div className="overflow-x-auto">
      <table
        className="min-w-full divide-y divide-neutral-200"
        aria-label={`${organizationName} transactions`}
      >
        <thead className="bg-neutral-50">
          <tr className="text-left text-small font-semibold text-neutral-600">
            <th scope="col" className="px-5 py-3">
              Created date
            </th>
            <th scope="col" className="px-5 py-3">
              Transaction type
            </th>
            <th scope="col" className="px-5 py-3">
              Change
            </th>
            <th scope="col" className="px-5 py-3">
              Balance after
            </th>
            <th scope="col" className="px-5 py-3">
              Interview or study
            </th>
            <th scope="col" className="px-5 py-3">
              Reference or source
            </th>
            <th scope="col" className="px-5 py-3">
              Tags
            </th>
          </tr>
        </thead>
        <tbody className="divide-y divide-neutral-100 bg-white">
          {transactions.map((row) => (
            <tr key={row.id} className="align-top">
              <td className="whitespace-nowrap px-5 py-4 text-body text-neutral-900">
                {formatDateTime(row.createdAt)}
              </td>
              <td className="px-5 py-4 text-body text-neutral-900">
                {formatTransactionTypeLabel(row.transactionType)}
              </td>
              <td
                className={[
                  'whitespace-nowrap px-5 py-4 text-body font-semibold tabular-nums',
                  row.quantity > 0 ? 'text-emerald-700' : 'text-neutral-900',
                ].join(' ')}
              >
                {formatQuantity(row.quantity)}
              </td>
              <td className="whitespace-nowrap px-5 py-4 text-body tabular-nums text-neutral-900">
                {row.resultingBalance}
              </td>
              <td className="px-5 py-4">{renderContext(row)}</td>
              <td className="px-5 py-4">{renderReference(row)}</td>
              <td className="px-5 py-4">
                <BackofficeTagChips tags={row.purchaseTags ?? []} />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
